import { mdiMenuRight } from '@mdi/js';
import Icon from '@mdi/react';
import { Box, MenuItem, Stack, SxProps, Typography } from '@mui/material';
import React, { Dispatch, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    downloadDocuments,
    downloadZip,
} from '../../../redux/project/file/sliceProjectFileDownload';
import { IAppReduxStore } from '../../../redux/store';
import { labelFontSemiBold, vars } from '../../../Theme';
import ActionButton from '../../General/ActionButton/ActionButton';
import ContextMenu from '../../General/ContextMenu/ContextMenu';
import { useEnqueueSnackbar } from '../../../hooks/useEnqueueSnackbar';
import { ToastSeverity } from '../../General/Toast/Toast';
import { useTranslation } from 'react-i18next';
import { defineActionButtonsAvailability, getToolTip } from '../../../util/DataUtils';
import AppTooltip from '../../General/AppTooltip/AppTooltip';

/** Styles for the  menu items */
export const menuItemSx: SxProps = {
    ...labelFontSemiBold,
    fontSize: 16,
    color: vars.font,
    height: '40px',
    '&.selected': {
        bgcolor: vars.brandGreen,
        color: vars.white,
    },
};

export const getMenuButtonLabelSx = (isOpen: boolean) => {
    return {
        ...labelFontSemiBold,
        fontSize: 16,
        ml: 1.5,
        color: isOpen ? vars.brandGreen : vars.font,
    };
};

export const getPopoverPlacement = (isFromKebab: boolean) =>
    isFromKebab
        ? ({
              anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: 'right',
              },
              transformOrigin: {
                  vertical: 'bottom',
                  horizontal: 'left',
              },
          } as const)
        : ({
              anchorOrigin: {
                  vertical: 'top',
                  horizontal: 'left',
              },
              transformOrigin: {
                  vertical: 'bottom',
                  horizontal: 'left',
              },
          } as const);

const getActionButtonSx = (isOpen: boolean) => {
    return {
        color: isOpen ? vars.brandGreen : vars.font,
        backgroundColor: isOpen ? vars.selectedBgGreen : vars.white,
    };
};

/** Enum for the download options */
enum EDownloadOption {
    Individual = 'individual',
    Zip = 'zipFile',
}

/** Items for the dropdown menu */
const options: Record<EDownloadOption, string> = {
    [EDownloadOption.Individual]: 'individualFiles',
    [EDownloadOption.Zip]: 'zipFile',
};

/**
 * Button Group Component that allows the user to choose a download method
 */
export default ({
    icon,
    text,
    isFromKebab = false,
    onClose,
    maxDocs,
    minDocs,
}: {
    icon: string;
    text: string;
    maxDocs: number;
    minDocs: number;
    isFromKebab?: boolean;
    onClose?: () => void;
}) => {
    const [anchorEl, setAnchorEl] = useState(null);

    /**State for the selected key in the dropdown menu */
    const [selectedKey, setSelectedKey] = React.useState(EDownloadOption.Individual);

    /**Ref for the group button and the popper */
    const anchorRef = React.useRef<HTMLDivElement>(null);

    /** Allows reducers on a redux slice to be called. */
    const dispatch: Dispatch<unknown> = useDispatch();

    /** Hook for customizing Snackbar. */
    const enqueueSnackbar = useEnqueueSnackbar();

    /** Access relevant state variables from the redux store. */
    const selectedDocuments = useSelector(
        (store: IAppReduxStore) => store.projectSelectedDocuments.storage,
    );

    /** Calculate the number of selected documents. */
    const totalDocuments = selectedDocuments?.documentIds?.length;

    /** Configure component translation. */
    const { t } = useTranslation('translations', { keyPrefix: 'projectView' });

    /**Download documents separately */
    const handleSingleDownloadDocuments = () => {
        dispatch(downloadDocuments(selectedDocuments));
        enqueueSnackbar(t('toastDownloadInfo', { numberOfDocuments: 1 }), {
            severity: ToastSeverity.Info,
        });
    };

    /**Download documents separately or as a zip  */
    const handleMenuItemClick = (
        event: React.MouseEvent<HTMLLIElement, MouseEvent>,
        key: EDownloadOption,
    ) => {
        setSelectedKey(key);
        key === EDownloadOption.Individual
            ? dispatch(downloadDocuments(selectedDocuments))
            : dispatch(downloadZip(selectedDocuments));
        setAnchorEl(null);
        enqueueSnackbar(
            t('toastDownloadInfo', { numberOfDocuments: selectedDocuments?.documentIds?.length }),
            {
                severity: ToastSeverity.Info,
            },
        );
    };

    /**Toggle the dropdonw menu */
    const handleToggle = (e: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(e.currentTarget as HTMLElement);
    };

    const handleOnDownloadButtonClick = (e: React.MouseEvent<HTMLElement>) => {
        totalDocuments > 1 ? handleToggle(e) : handleSingleDownloadDocuments();
    };

    /**Close the dropdown menu */
    const handleClose = () => {
        setAnchorEl(null);
        onClose?.();
    };

    const menuItemColor = anchorEl ? vars.brandGreen : vars.font;

    const arrowIcon = useMemo(() => {
        return totalDocuments > 1 ? (
            <Stack
                direction='row'
                sx={{ display: 'flex', justifyContent: 'flex-end', flexGrow: 4 }}
            >
                <Icon color={menuItemColor} path={mdiMenuRight} size={1} />
            </Stack>
        ) : null;
    }, [totalDocuments, menuItemColor]);

    const isActionButtionDisabled = useMemo(
        () => defineActionButtonsAvailability(totalDocuments, maxDocs, minDocs),
        [totalDocuments],
    );

    return (
        <>
            {isFromKebab ? (
                <AppTooltip
                    title={
                        isActionButtionDisabled
                            ? t(getToolTip(maxDocs, minDocs), { maxDocs, minDocs })
                            : null
                    }
                    placement='top-start'
                >
                    <Box>
                        <MenuItem
                            key={text}
                            sx={menuItemSx}
                            selected={!!anchorEl}
                            onClick={handleOnDownloadButtonClick}
                            disabled={isActionButtionDisabled}
                        >
                            <Stack
                                direction='row'
                                sx={{ display: 'flex', width: '100%' }}
                                ref={anchorRef}
                            >
                                <Icon color={menuItemColor} path={icon} size={1} />
                                <Typography sx={getMenuButtonLabelSx(!!anchorEl)}>
                                    {text}
                                </Typography>
                                {arrowIcon}
                            </Stack>
                        </MenuItem>
                    </Box>
                </AppTooltip>
            ) : (
                <AppTooltip
                    title={
                        isActionButtionDisabled
                            ? t(getToolTip(maxDocs, minDocs), { maxDocs, minDocs })
                            : null
                    }
                    placement='top-start'
                >
                    <Box>
                        <ActionButton
                            sx={getActionButtonSx(!!anchorEl)}
                            icon={icon}
                            text={text}
                            onClick={handleOnDownloadButtonClick}
                            disabled={isActionButtionDisabled}
                            color={isActionButtionDisabled ? vars.disabledGrey : vars.font}
                        />
                    </Box>
                </AppTooltip>
            )}
            <ContextMenu
                anchorEl={anchorEl}
                open={!!anchorEl}
                onClick={handleClose}
                onClose={handleClose}
                {...getPopoverPlacement(isFromKebab)}
            >
                {Object.values(EDownloadOption).map(option => (
                    <MenuItem
                        key={option}
                        sx={menuItemSx}
                        selected={option === selectedKey}
                        onClick={event => handleMenuItemClick(event, option)}
                    >
                        {t(options[option])}
                    </MenuItem>
                ))}
            </ContextMenu>
        </>
    );
};
